import React, { useState, useEffect, useRef, useCallback } from 'react';
import xService from '../services/xService';
import MarkdownRenderer from './MarkdownRenderer';
import { textToHtml } from './SummaryEditor';

/**
 * Dialog component for displaying AI analysis of X search results.
 * 
 * Handles polling the bedrock service until analysis is complete,
 * then displays the results in a formatted dialog.
 */
export default function XAnalysisDialog({ show, onClose, resultId, jobId: initialJobId, searchName }) {
    const [jobId, setJobId] = useState(initialJobId);
    const [status, setStatus] = useState('pending');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [pollingActive, setPollingActive] = useState(false);
    const analysisStartedRef = useRef(false);
    const pollingIntervalRef = useRef(null);
    const pollingTimeoutRef = useRef(null);

    // Start analysis job when dialog opens (only once when show becomes true)
    useEffect(() => {
        if (show && !jobId && resultId && !analysisStartedRef.current) {
            analysisStartedRef.current = true;
            startAnalysis();
        }

        // Reset when dialog closes
        if (!show) {
            analysisStartedRef.current = false;
        }
    }, [show, jobId, resultId]);

    // Stop polling function
    const stopPolling = useCallback(() => {
        console.log('Stopping polling - clearing timers');
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
            console.log('Cleared timeout');
        }
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            console.log('Cleared interval');
        }
        setPollingActive(false);
    }, []);

    // Check status function
    const checkStatus = useCallback(async () => {
        if (!jobId || !resultId) return;

        console.log('Checking analysis status...');
        try {
            const response = await xService.getAnalysisStatus(resultId, jobId);

            console.log('Status response:', response.status);
            setStatus(response.status);

            if (response.status === 'completed') {
                console.log('Analysis completed! Stopping polling...');
                setResult(response.result);
                stopPolling();
            } else if (response.status === 'failed') {
                console.log('Analysis failed! Stopping polling...');
                setError(response.error || 'Analysis failed');
                stopPolling();
            }
        } catch (err) {
            console.error('Error checking analysis status:', err);
            // Don't stop polling on transient errors
            if (err.response?.status === 404) {
                console.log('Job not found (404)! Stopping polling...');
                setError('Analysis job not found');
                setStatus('failed');
                stopPolling();
            }
        }
    }, [jobId, resultId, stopPolling]);

    // Poll for status when we have a job ID
    useEffect(() => {
        if (!show || !jobId) return;

        setPollingActive(true);

        // Wait 5 seconds before first check to give the job time to process
        pollingTimeoutRef.current = setTimeout(() => {
            checkStatus();
        }, 5000);

        // Then poll every 10 seconds
        pollingIntervalRef.current = setInterval(() => {
            checkStatus();
        }, 10000); // Poll every 10 seconds to reduce server load

        return () => {
            stopPolling();
        };
    }, [show, jobId, checkStatus, stopPolling]);

    const startAnalysis = async () => {
        try {
            setStatus('starting');
            setError(null);

            const response = await xService.analyzeResult(resultId);

            if (response.success && response.job_id) {
                setJobId(response.job_id);
            } else {
                setError('Failed to start analysis job');
                setStatus('failed');
            }
        } catch (err) {
            console.error('Error starting analysis:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to start analysis');
            setStatus('failed');
        }
    };

    const handleClose = () => {
        stopPolling();
        // Reset state for next opening
        setJobId(initialJobId);
        setStatus('pending');
        setResult(null);
        setError(null);
        analysisStartedRef.current = false;
        onClose();
    };

    const handleCopyToClipboard = async () => {
        if (!result) return;

        try {
            await navigator.clipboard.writeText(result);
            
            // Show success toast
            const toast = document.createElement('div');
            toast.className = 'alert alert-success position-fixed';
            toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            toast.innerHTML = '<i class="bi bi-check-circle me-2"></i>Copied to clipboard!';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            
            // Show error toast
            const toast = document.createElement('div');
            toast.className = 'alert alert-danger position-fixed';
            toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            toast.innerHTML = '<i class="bi bi-exclamation-circle me-2"></i>Failed to copy to clipboard';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
        }
    };

    const handleExportToPdf = async () => {
        if (!result) return;

        try {
            // Show loading toast
            const loadingToast = document.createElement('div');
            loadingToast.className = 'alert alert-info position-fixed';
            loadingToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            loadingToast.innerHTML = '<div class="spinner-border spinner-border-sm me-2" role="status"></div>Generating PDF...';
            document.body.appendChild(loadingToast);

            // Helper function to replace emojis with text or remove them
            const cleanTextForPdf = (text) => {
                if (!text) return '';
                
                // Common emoji mappings to text equivalents
                const emojiMap = {
                    '✅': '[CHECK] ',
                    '❌': '[X] ',
                    '⚠️': '[WARNING] ',
                    '⚠': '[WARNING] ',
                    '📊': '[CHART] ',
                    '📈': '[TRENDING UP] ',
                    '📉': '[TRENDING DOWN] ',
                    '💡': '[IDEA] ',
                    '🔥': '[FIRE] ',
                    '👍': '[THUMBS UP] ',
                    '👎': '[THUMBS DOWN] ',
                    '⭐': '[STAR] ',
                    '🎯': '[TARGET] ',
                    '📌': '[PIN] ',
                    '🚀': '[ROCKET] ',
                    '💰': '[MONEY] ',
                    '📱': '[PHONE] ',
                    '💻': '[COMPUTER] ',
                    '🌐': '[GLOBE] ',
                    '📧': '[EMAIL] ',
                    '🔔': '[BELL] ',
                    '⚡': '[LIGHTNING] ',
                    '🎉': '[CELEBRATION] ',
                    '💬': '[COMMENT] ',
                    '📝': '[NOTE] ',
                    '🔍': '[SEARCH] ',
                    '🏆': '[TROPHY] ',
                    '💼': '[BRIEFCASE] ',
                    '📅': '[CALENDAR] ',
                    '⏰': '[ALARM] ',
                    '⏸️': '[PAUSE] ',
                    '⏸': '[PAUSE] ',
                    '▶️': '[PLAY] ',
                    '▶': '[PLAY] ',
                    '⏹️': '[STOP] ',
                    '⏹': '[STOP] ',
                    '⏺️': '[RECORD] ',
                    '⏺': '[RECORD] ',
                    '✨': '[SPARKLES] ',
                    '🎨': '[ART] ',
                    '🔗': '[LINK] ',
                    '📢': '[ANNOUNCEMENT] ',
                    '🌟': '[STAR] ',
                    '💪': '[STRONG] ',
                    '🎓': '[EDUCATION] ',
                    '🔒': '[LOCKED] ',
                    '🔓': '[UNLOCKED] ',
                    '➡️': '-> ',
                    '➡': '-> ',
                    '⬅️': '<- ',
                    '⬅': '<- ',
                    '⬆️': '^ ',
                    '⬆': '^ ',
                    '⬇️': 'v ',
                    '⬇': 'v ',
                    '➕': '+ ',
                    '➖': '- ',
                    '✖️': 'x ',
                    '✖': 'x ',
                    '➗': '/ ',
                };
                
                let cleaned = text;
                
                // Replace known emojis with text equivalents
                Object.entries(emojiMap).forEach(([emoji, replacement]) => {
                    cleaned = cleaned.split(emoji).join(replacement);
                });
                
                // Remove any remaining emojis (Unicode emoji ranges)
                // This regex covers most common emojis
                cleaned = cleaned.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{FE00}-\u{FE0F}]|[\u{200D}]/gu, '');
                
                return cleaned;
            };

            // Dynamic import of jsPDF
            const { default: jsPDF } = await import('jspdf');

            // Create PDF
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 60;
            const maxWidth = pageWidth - (2 * margin);
            let yPosition = margin;

            // Helper function to add text with word wrapping and page breaks
            const addText = (text, fontSize, isBold = false, color = [0, 0, 0], lineSpacing = 1.4) => {
                pdf.setFontSize(fontSize);
                pdf.setTextColor(...color);

                // Clean text for PDF rendering
                const cleanedText = cleanTextForPdf(text);
                
                // Check if text contains markdown bold **text**
                const hasBoldMarkdown = /\*\*[^*]+\*\*/.test(cleanedText);
                
                if (hasBoldMarkdown && !isBold) {
                    // Process text with inline bold formatting
                    const segments = [];
                    let remaining = cleanedText;
                    let currentPos = 0;
                    
                    // Split text by **bold** markers
                    const boldRegex = /\*\*([^*]+)\*\*/g;
                    let match;
                    let lastIndex = 0;
                    
                    while ((match = boldRegex.exec(cleanedText)) !== null) {
                        // Add text before bold
                        if (match.index > lastIndex) {
                            segments.push({ text: cleanedText.substring(lastIndex, match.index), bold: false });
                        }
                        // Add bold text
                        segments.push({ text: match[1], bold: true });
                        lastIndex = match.index + match[0].length;
                    }
                    
                    // Add remaining text
                    if (lastIndex < cleanedText.length) {
                        segments.push({ text: cleanedText.substring(lastIndex), bold: false });
                    }
                    
                    // Render segments with mixed formatting
                    const lineHeight = fontSize * lineSpacing;
                    let currentLine = '';
                    let currentLineSegments = [];
                    
                    for (const segment of segments) {
                        const words = segment.text.split(' ');
                        
                        for (let i = 0; i < words.length; i++) {
                            const word = words[i] + (i < words.length - 1 ? ' ' : '');
                            const testLine = currentLine + word;
                            
                            pdf.setFont('helvetica', 'normal');
                            const testWidth = pdf.getTextWidth(testLine);
                            
                            if (testWidth > maxWidth && currentLine !== '') {
                                // Render current line
                                if (yPosition + lineHeight > pageHeight - margin) {
                                    pdf.addPage();
                                    yPosition = margin;
                                }
                                
                                let xPos = margin;
                                for (const lineSeg of currentLineSegments) {
                                    pdf.setFont('helvetica', lineSeg.bold ? 'bold' : 'normal');
                                    pdf.text(lineSeg.text, xPos, yPosition);
                                    xPos += pdf.getTextWidth(lineSeg.text);
                                }
                                
                                yPosition += lineHeight;
                                currentLine = word;
                                currentLineSegments = [{ text: word, bold: segment.bold }];
                            } else {
                                currentLine = testLine;
                                if (currentLineSegments.length > 0 && 
                                    currentLineSegments[currentLineSegments.length - 1].bold === segment.bold) {
                                    // Append to last segment if same style
                                    currentLineSegments[currentLineSegments.length - 1].text += word;
                                } else {
                                    currentLineSegments.push({ text: word, bold: segment.bold });
                                }
                            }
                        }
                    }
                    
                    // Render last line
                    if (currentLine !== '') {
                        if (yPosition + lineHeight > pageHeight - margin) {
                            pdf.addPage();
                            yPosition = margin;
                        }
                        
                        let xPos = margin;
                        for (const lineSeg of currentLineSegments) {
                            pdf.setFont('helvetica', lineSeg.bold ? 'bold' : 'normal');
                            pdf.text(lineSeg.text, xPos, yPosition);
                            xPos += pdf.getTextWidth(lineSeg.text);
                        }
                        
                        yPosition += lineHeight;
                    }
                } else {
                    // Simple text without inline formatting
                    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
                    const lines = pdf.splitTextToSize(cleanedText, maxWidth);
                    const lineHeight = fontSize * lineSpacing;

                    lines.forEach(line => {
                        if (yPosition + lineHeight > pageHeight - margin) {
                            pdf.addPage();
                            yPosition = margin;
                        }
                        pdf.text(line, margin, yPosition);
                        yPosition += lineHeight;
                    });
                }

                return yPosition;
            };

            // Helper function to add spacing
            const addSpacing = (space) => {
                yPosition += space;
                if (yPosition > pageHeight - margin) {
                    pdf.addPage();
                    yPosition = margin;
                }
            };

            // Add header with title
            pdf.setFillColor(0, 102, 204);
            pdf.rect(0, 0, pageWidth, 80, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(24);
            pdf.setFont('helvetica', 'bold');
            pdf.text('X Search Analysis', margin, 50);
            yPosition = 100;

            // Add search name
            if (searchName) {
                pdf.setTextColor(100, 100, 100);
                pdf.setFontSize(12);
                pdf.setFont('helvetica', 'normal');
                pdf.text(`Search: ${searchName}`, margin, yPosition);
                yPosition += 30;
            }

            // Add timestamp
            pdf.setTextColor(100, 100, 100);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            const timestamp = new Date().toLocaleString();
            pdf.text(`Generated: ${timestamp}`, margin, yPosition);
            yPosition += 30;

            // Process the markdown result
            const lines = result.split('\n');

            for (let i = 0; i < lines.length; i++) {
                let line = lines[i];

                // Skip empty lines but add spacing
                if (!line.trim()) {
                    addSpacing(8);
                    continue;
                }

                // Handle headers
                if (line.startsWith('####')) {
                    addSpacing(5);
                    addText(line.replace(/^####\s*/, ''), 11, true, [0, 102, 204]);
                    addSpacing(8);
                } else if (line.startsWith('###')) {
                    addSpacing(8);
                    addText(line.replace(/^###\s*/, ''), 13, true, [0, 102, 204]);
                    addSpacing(10);
                } else if (line.startsWith('##')) {
                    addSpacing(12);
                    addText(line.replace(/^##\s*/, ''), 15, true, [0, 102, 204]);
                    addSpacing(12);
                } else if (line.startsWith('#')) {
                    addSpacing(15);
                    addText(line.replace(/^#\s*/, ''), 18, true, [0, 102, 204]);
                    addSpacing(15);
                } else if (line.startsWith('- ') || line.startsWith('* ')) {
                    // Bullet points
                    const bulletText = line.replace(/^[-*]\s*/, '• ');
                    addText(bulletText, 11, false, [0, 0, 0], 1.5);
                } else if (/^\d+\.\s/.test(line)) {
                    // Numbered lists
                    addText(line, 11, false, [0, 0, 0], 1.5);
                } else if (line.startsWith('> ')) {
                    // Block quotes
                    const quoteText = line.replace(/^>\s*/, '');
                    addText(quoteText, 10, false, [100, 100, 100], 1.5);
                } else if (line.trim().startsWith('```')) {
                    // Code blocks - just skip the markers for now
                    continue;
                } else {
                    // Regular paragraph text
                    addText(line, 11, false, [0, 0, 0], 1.5);
                }
            }

            // Save the PDF
            const fileName = `X_Analysis_${searchName ? searchName.replace(/[^a-z0-9]/gi, '_') : 'Report'}_${new Date().getTime()}.pdf`;
            pdf.save(fileName);

            // Remove loading toast and show success
            loadingToast.remove();
            const successToast = document.createElement('div');
            successToast.className = 'alert alert-success position-fixed';
            successToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            successToast.innerHTML = '<i class="bi bi-check-circle me-2"></i>PDF downloaded successfully!';
            document.body.appendChild(successToast);
            setTimeout(() => successToast.remove(), 3000);
        } catch (err) {
            console.error('PDF export failed:', err);

            // Show error toast
            const errorToast = document.createElement('div');
            errorToast.className = 'alert alert-danger position-fixed';
            errorToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            errorToast.innerHTML = '<i class="bi bi-exclamation-circle me-2"></i>PDF export failed. Please try again.';
            document.body.appendChild(errorToast);
            setTimeout(() => errorToast.remove(), 3000);
        }
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg" style={{ maxHeight: '90vh' }}>
                <div className="modal-content" style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="bi bi-stars me-2"></i>
                            AI Analysis: {searchName}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleClose}
                            aria-label="Close"
                        ></button>
                    </div>

                    <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                        {renderContent()}
                    </div>

                    <div className="modal-footer">
                        <div className="d-flex justify-content-between w-100 align-items-center">
                            <div className="d-flex gap-2">
                                {status === 'completed' && result && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={handleCopyToClipboard}
                                            title="Copy to clipboard"
                                        >
                                            <i className="bi bi-clipboard me-2"></i>
                                            Copy
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-success"
                                            onClick={handleExportToPdf}
                                            title="Export to PDF"
                                        >
                                            <i className="bi bi-file-pdf me-2"></i>
                                            Export PDF
                                        </button>
                                    </>
                                )}
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleClose}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    function renderContent() {
        if (error) {
            return (
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>Error:</strong> {error}
                </div>
            );
        }

        if (status === 'starting' || status === 'pending') {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Starting analysis...</p>
                </div>
            );
        }

        if (status === 'processing') {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Processing...</span>
                    </div>
                    <p className="text-muted">Analyzing search results with AI...</p>
                    <small className="text-muted">This may take a moment.</small>
                </div>
            );
        }

        if (status === 'completed' && result) {
            return (
                <div className="analysis-result h-100">
                    <div
                        className="analysis-text p-3 border rounded bg-light h-100"
                        style={{
                            overflowY: 'auto',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            lineHeight: '1.6'
                        }}
                    >
                        <MarkdownRenderer content={result} />
                    </div>
                </div>
            );
        }

        return (
            <div className="text-center py-5 text-muted">
                <p>Waiting for analysis to start...</p>
            </div>
        );
    }
}
